// frontend/src/components/portfolio/PortfolioEditor.tsx
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, CircularProgress, Typography, Button, Paper } from '@mui/material';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { usePortfolio } from '../../hooks/usePortfolio';
import EditorHeader from './EditorHeader';
import BlockLibrary from './BlockLibrary';
import BlockCanvas from './BlockCanvas';
import BlockEditModal from './BlockEditModal';
import InvestorContextPanel from '../portfolio/InvestorContextPanel';

const PortfolioEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  
  const {
    portfolio,
    loading,
    error,
    hasUnsavedChanges,
    lastSaved,
    saving,
    selectedBlockId,
    addBlock,
    removeBlock,
    updateBlock,
    reorderBlocks,
    toggleBlockEnabled,
    selectBlock,
    updatePortfolioMeta,
    save,
    refresh
  } = usePortfolio(id || '');

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !portfolio) {
      return;
    }

    const oldIndex = portfolio.sections.findIndex(block => block.id === active.id);
    const newIndex = portfolio.sections.findIndex(block => block.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newSections = [...portfolio.sections];
    const [movedBlock] = newSections.splice(oldIndex, 1);
    newSections.splice(newIndex, 0, movedBlock);

    reorderBlocks(newSections);
  };

  const handleOpenEditModal = (blockId: string) => {
    setEditingBlockId(blockId);
    selectBlock(blockId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingBlockId(null);
  };

  const handleSaveBlock = (blockId: string, data: any) => {
    updateBlock(blockId, data);
  };

  const handleDeleteBlock = (blockId: string) => {
    removeBlock(blockId);
  };

  if (!id) {
    return (
      <Box p={3}>
        <Typography color="error">Portfolio ID is required</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="#2C3E50"
      >
        <CircularProgress size={60} sx={{ color: '#3A9188' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="#2C3E50"
        gap={2}
      >
        <Typography variant="h5" color="#E57373">
          {error}
        </Typography>
        <Button variant="contained" onClick={refresh}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!portfolio) {
    return (
      <Box p={3}>
        <Typography color="error">Portfolio not found</Typography>
      </Box>
    );
  }

  const selectedBlock = editingBlockId 
    ? portfolio.sections.find(block => block.id === editingBlockId)
    : undefined;

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#2C3E50',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable inner wrapper */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#3A9188', borderRadius: '4px' },
        }}
      >
        {/* Header */}
        <EditorHeader
          portfolio={portfolio}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          saving={saving}
          onSave={save}
          onUpdateMeta={updatePortfolioMeta}
        />

        {/* Investor Context */}
        <InvestorContextPanel
          investorContext={portfolio.investorContext}
          onSave={async (data) => {
            await updatePortfolioMeta({ investorContext: data });
          }}
        />

        {/* Main Editor Area - NOW ONLY 2 COLUMNS */}
        <Box sx={{ flex: 1, p: 2 }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Left Panel - Block Library */}
            <Grid item sx={{ width: 280 }}>
              <Paper
                elevation={1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#34495E',
                  border: '1px solid #3A9188',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  <BlockLibrary onAddBlock={addBlock} />
                </Box>
              </Paper>
            </Grid>

            {/* Center Panel - Block Canvas (NOW TAKES FULL WIDTH) */}
            <Grid item xs>
              <Paper
                elevation={1}
                sx={{
                  height: '100%',
                  overflow: 'hidden',
                  bgcolor: '#34495E',
                  border: '1px solid #3A9188',
                }}
              >
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={portfolio.sections.map((block) => block.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <BlockCanvas
                      sections={portfolio.sections}
                      selectedBlockId={selectedBlockId}
                      portfolioId={portfolio.id || (portfolio as any)._id}  // ✅ Extract from portfolio object
                      portfolioTitle={portfolio.title}       // ← ADD THIS
                      onSelectBlock={selectBlock}
                      onRemoveBlock={removeBlock}
                      onToggleEnabled={toggleBlockEnabled}
                      onOpenEditModal={handleOpenEditModal}
                    />
                  </SortableContext>
                </DndContext>
              </Paper>
            </Grid>

            {/* RIGHT PANEL REMOVED - Now using modal */}
          </Grid>
        </Box>
      </Box>

      {/* Edit Modal - Full Screen with AUTO-SAVE */}
      <BlockEditModal
        open={editModalOpen}
        block={selectedBlock || null}
        onClose={handleCloseEditModal}
        onSave={handleSaveBlock}
        onDelete={handleDeleteBlock}
        onAutoSave={save} // ✅ Pass the save function for auto-saving
      />
    </Box>
  );
};

export default PortfolioEditor;